import {
  Component,
  effect,
  HostListener,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';

const ANIM_MS = 320;

@Component({
  selector: 'ui-drawer',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (rendered()) {
      <div
        class="ui-drawer"
        [class.ui-drawer--open]="open()"
        role="dialog"
        aria-modal="true"
        [attr.aria-label]="title() || 'Painel'">
        <div class="ui-drawer__backdrop" (click)="requestClose()" aria-hidden="true"></div>
        <aside
          class="ui-drawer__panel"
          [class.ui-drawer__panel--left]="placement() === 'left'"
          [class.ui-drawer__panel--right]="placement() === 'right'"
          [style.width]="width()">
          @if (title()) {
            <header class="ui-drawer__header">
              <h3 class="ui-drawer__title">{{ title() }}</h3>
              <button
                type="button"
                class="ui-drawer__close"
                aria-label="Fechar"
                (click)="requestClose()">
                &times;
              </button>
            </header>
          }
          <div class="ui-drawer__body">
            <ng-content />
          </div>
        </aside>
      </div>
    }
  `,
  styles: [`
    .ui-drawer {
      position: fixed;
      inset: 0;
      z-index: 50;
    }

    .ui-drawer__backdrop {
      position: absolute;
      inset: 0;
      background: rgba(15, 23, 42, 0.45);
      opacity: 0;
      transition: opacity ${ANIM_MS}ms ease;
      backdrop-filter: blur(2px);
    }

    .ui-drawer--open .ui-drawer__backdrop {
      opacity: 1;
    }

    .ui-drawer__panel {
      position: absolute;
      top: 0;
      bottom: 0;
      display: flex;
      flex-direction: column;
      max-width: 100vw;
      background: var(--surface-bg, #fff);
      box-shadow: 0 0 40px rgba(15, 23, 42, 0.18);
      overflow: hidden;
      transition: transform ${ANIM_MS}ms cubic-bezier(0.32, 0.72, 0, 1);
      will-change: transform;
    }

    .ui-drawer__panel--left {
      left: 0;
      transform: translate3d(-100%, 0, 0);
    }

    .ui-drawer__panel--right {
      right: 0;
      transform: translate3d(100%, 0, 0);
    }

    .ui-drawer--open .ui-drawer__panel--left,
    .ui-drawer--open .ui-drawer__panel--right {
      transform: translate3d(0, 0, 0);
    }

    .ui-drawer__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      flex-shrink: 0;
      padding: 0.875rem 1.25rem;
      border-bottom: 1px solid var(--border-color, #e8e8e8);
      background: linear-gradient(
        180deg,
        var(--surface-bg, #fff) 0%,
        color-mix(in srgb, var(--surface-muted, #fafafa) 40%, var(--surface-bg, #fff)) 100%
      );
    }

    .ui-drawer__title {
      margin: 0;
      font-size: 1.0625rem;
      font-weight: 700;
      letter-spacing: -0.02em;
      color: var(--text-primary, #1f1f1f);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ui-drawer__close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border: 0;
      border-radius: 0.5rem;
      background: transparent;
      color: var(--text-muted, #8c8c8c);
      font-size: 1.5rem;
      line-height: 1;
      cursor: pointer;
      transition: background 0.15s, color 0.15s;
    }

    .ui-drawer__close:hover {
      background: var(--surface-muted, #f5f5f5);
      color: var(--text-primary, #1f1f1f);
    }

    .ui-drawer__body {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding: 1.25rem;
    }

    @media (max-width: 767px) {
      .ui-drawer__panel {
        width: 100% !important;
        max-width: 100%;
      }

      .ui-drawer__body {
        padding: 1rem;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .ui-drawer__backdrop,
      .ui-drawer__panel {
        transition: none;
      }
    }
  `],
})
export class UiDrawerComponent implements OnDestroy {
  visible = input(false);
  title = input<string | null>(null);
  placement = input<'left' | 'right'>('left');
  width = input('280px');
  onClose = output<void>();

  protected readonly rendered = signal(false);
  protected readonly open = signal(false);

  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const visible = this.visible();
      this.clearCloseTimer();

      if (visible) {
        this.rendered.set(true);
        this.lockBodyScroll(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.open.set(true));
        });
        return;
      }

      if (this.rendered()) {
        this.open.set(false);
        this.lockBodyScroll(false);
        this.closeTimer = setTimeout(() => {
          this.rendered.set(false);
          this.closeTimer = null;
        }, ANIM_MS);
      }
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.rendered() && this.open()) {
      this.requestClose();
    }
  }

  requestClose(): void {
    this.onClose.emit();
  }

  ngOnDestroy(): void {
    this.clearCloseTimer();
    this.lockBodyScroll(false);
  }

  private clearCloseTimer(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private lockBodyScroll(lock: boolean): void {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = lock ? 'hidden' : '';
  }
}
